import { create } from 'zustand';
import type { EyeTraxSample, TimelineSeg } from '../types';
import type { FaceAnchor } from '../lib/faceSelection';

type SessionState = {
  stream: MediaStream | null;
  hasPermission: boolean;
  faceOK: boolean;
  selectedFaceAnchor: FaceAnchor | null;
  samples: EyeTraxSample[];
  timeline: TimelineSeg[];
  videoBlob: Blob | null;
  videoMimeType: string | null;
  videoBytes: number;
  _t0: number | null;
  setStream: (s: MediaStream | null) => void;
  setHasPermission: (b: boolean) => void;
  setFaceOK: (b: boolean) => void;
  setSelectedFaceAnchor: (anchor: FaceAnchor | null) => void;
  addSample: (s: EyeTraxSample) => void;
  startTimelineIfNeeded: (nowMs: number) => void;
  startSegment: (mediaIndex: number, nowMs: number) => void;
  endActiveSegment: (nowMs: number) => void;
  setVideoArtifact: (blob: Blob | null, mimeType?: string | null) => void;
  clear: () => void;
};

export const useSession = create<SessionState>((set) => ({
  stream: null,
  hasPermission: false,
  faceOK: false,
  selectedFaceAnchor: null,
  samples: [],
  timeline: [],
  videoBlob: null,
  videoMimeType: null,
  videoBytes: 0,
  _t0: null,
  setStream: (s) => set({ stream: s }),
  setHasPermission: (b) => set({ hasPermission: b }),
  setFaceOK: (b) => set({ faceOK: b }),
  setSelectedFaceAnchor: (anchor) => set({ selectedFaceAnchor: anchor }),
  addSample: (s) =>
    set((st) => {
      // keep a moving window if you want to limit memory during dev:
      // const cap = 30000; if (st.samples.length >= cap) st.samples.shift();
      st.samples.push(s);
      return { samples: st.samples };
    }),
  startTimelineIfNeeded: (nowMs) =>
    set((st) => (st._t0 == null ? { _t0: nowMs, timeline: [] } : {})),
  startSegment: (mediaIndex, nowMs) =>
    set((st) => {
      const t0 = st._t0 ?? nowMs;
      const start_ms = Math.max(0, Math.round(nowMs - t0));
      const tl = st.timeline.slice();
      if (tl.length && tl[tl.length - 1].end_ms < tl[tl.length - 1].start_ms) {
        tl[tl.length - 1].end_ms = start_ms;
      }
      tl.push({ mediaIndex, start_ms, end_ms: start_ms - 1 });
      return { _t0: t0, timeline: tl };
    }),
  endActiveSegment: (nowMs) =>
    set((st) => {
      if (!st.timeline.length) return {};
      const t0 = st._t0 ?? nowMs;
      const end_ms = Math.max(0, Math.round(nowMs - t0));
      const tl = st.timeline.slice();
      if (tl[tl.length - 1].end_ms < tl[tl.length - 1].start_ms) {
        tl[tl.length - 1].end_ms = end_ms;
      }
      return { timeline: tl };
    }),
  setVideoArtifact: (blob, mimeType) =>
    set({
      videoBlob: blob,
      videoMimeType: blob ? mimeType ?? blob.type ?? null : null,
      videoBytes: blob ? blob.size : 0,
    }),
  clear: () =>
    set({
      samples: [],
      timeline: [],
      videoBlob: null,
      videoMimeType: null,
      videoBytes: 0,
      _t0: null,
      faceOK: false,
      selectedFaceAnchor: null,
    }),
}));
